import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { canFeature } from '@/lib/perms';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit2, Copy, Star, Archive, RotateCcw, ChevronUp, ChevronDown, Layers } from 'lucide-react';
import TemplateBOQEditor from '@/components/TemplateBOQEditor';
import { useConfirm } from '@/components/ConfirmDialog';

// ─── Main Templates Manager ─────────────────────────────
export default function TemplatesManager() {
  const { user } = useAuth();
  const [confirm, confirmDialog] = useConfirm();
  const isAdmin = canFeature(user, 'manageData');
  const [categories, setCategories] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showCatModal, setShowCatModal] = useState(false);
  const [catName, setCatName] = useState('');
  const [editingCat, setEditingCat] = useState(null);
  const [showTmplModal, setShowTmplModal] = useState(false);
  const [tmplName, setTmplName] = useState('');

  const load = useCallback(async () => {
    try {
      const [catRes, tmplRes] = await Promise.all([
        api.get('/template-categories', { params: { include_archived: showArchived } }),
        api.get('/templates', { params: { include_archived: showArchived } }),
      ]);
      const cats = catRes.data || [];
      setCategories(cats);
      setTemplates(tmplRes.data || []);
      setActiveCat(prev => prev && cats.some(c => c.id === prev) ? prev : (cats[0]?.id || ''));
    } catch { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
  }, [showArchived]);

  useEffect(() => { load(); }, [load]);

  const seed = async () => {
    try { const res = await api.post('/templates/seed'); toast.success(res.data.message); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Seed failed'); }
  };

  // Category actions
  const saveCat = async () => {
    if (!catName.trim()) { toast.error('Category name required'); return; }
    try {
      if (editingCat) { await api.put(`/template-categories/${editingCat.id}`, { name: catName.trim() }); toast.success('Category updated'); }
      else { await api.post('/template-categories', { name: catName.trim() }); toast.success('Category created'); }
      setShowCatModal(false); setCatName(''); setEditingCat(null); load();
    } catch { toast.error('Failed to save category'); }
  };
  const duplicateCat = async (id) => { try { await api.post(`/template-categories/${id}/duplicate`); toast.success('Category duplicated'); load(); } catch { toast.error('Failed'); } };
  const setCatStatus = async (id, status) => { try { await api.put(`/template-categories/${id}`, { status }); toast.success(status === 'archived' ? 'Category archived' : 'Category restored'); load(); } catch { toast.error('Failed'); } };
  const moveCat = async (idx, dir) => {
    const arr = [...categories]; const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setCategories(arr);
    try { await api.put('/template-categories/reorder', { category_ids: arr.map(c => c.id) }); } catch { toast.error('Failed to reorder'); load(); }
  };

  // Template actions
  const createTemplate = async () => {
    if (!tmplName.trim()) { toast.error('Template name required'); return; }
    try { await api.post('/templates', { name: tmplName.trim(), category_id: activeCat, rooms: [] }); toast.success('Template created'); setShowTmplModal(false); setTmplName(''); load(); }
    catch { toast.error('Failed to create template'); }
  };
  const duplicateTemplate = async (id) => { try { await api.post(`/templates/${id}/duplicate`); toast.success('Template duplicated'); load(); } catch { toast.error('Failed'); } };
  const setDefault = async (id) => { try { await api.post(`/templates/${id}/set-default`); toast.success('Default template set'); load(); } catch { toast.error('Failed'); } };
  const archiveTemplate = async (id) => {
    if (!(await confirm({ title: 'Archive template?', message: 'It will be moved out of the active template list.', confirmLabel: 'Archive' }))) return;
    try { await api.delete(`/templates/${id}`); toast.success('Template archived'); load(); } catch { toast.error('Failed'); }
  };
  const restoreTemplate = async (id) => { try { await api.put(`/templates/${id}`, { status: 'active' }); toast.success('Template restored'); load(); } catch { toast.error('Failed'); } };

  if (loading) return <div className="flex items-center justify-center h-32"><div className="w-5 h-5 border-2 border-[#3A8C8C] border-t-transparent rounded-full animate-spin" /></div>;

  const catTemplates = templates.filter(t => t.category_id === activeCat);

  if (categories.length === 0) {
    return (
      <div className="text-center py-12" data-testid="templates-empty">
        <Layers size={32} strokeWidth={1.5} className="text-[#D4D0C8] mx-auto mb-3" />
        <p className="text-sm text-[#6B7280]">No template categories yet</p>
        {isAdmin && <button onClick={seed} className="text-xs text-[#3A8C8C] hover:underline mt-2" data-testid="templates-seed-btn">Load default categories &amp; templates</button>}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="templates-manager">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-base font-semibold text-[#2C2C2E] font-['Manrope']">BOQ Templates</h3>
          <p className="text-xs text-[#6B7280] mt-0.5">{templates.filter(t => t.status !== 'archived').length} active templates · {categories.length} categories</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-[#6B7280] cursor-pointer">
            <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} data-testid="templates-show-archived" />
            Show archived
          </label>
          {isAdmin && (
            <button onClick={() => { setEditingCat(null); setCatName(''); setShowCatModal(true); }} data-testid="add-category-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#E8E6E1] text-[#2C2C2E] text-xs font-medium hover:bg-[#D4D0C8] transition-colors">
              <Plus size={13} /> Category
            </button>
          )}
        </div>
      </div>

      {/* Category pills */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="category-pills">
        {categories.map((c, idx) => (
          <div key={c.id} className={`group flex items-center gap-1 pl-3 pr-1.5 py-1 rounded-full text-xs font-medium transition-all cursor-pointer ${activeCat === c.id ? 'bg-[#3A8C8C] text-white' : 'bg-[#E8E6E1] text-[#2C2C2E] hover:bg-[#D4D0C8]'}`}>
            <button onClick={() => setActiveCat(c.id)} data-testid={`category-pill-${idx}`} className="flex items-center gap-1.5">
              {c.name}
              {c.status === 'archived' && <span className={`text-[9px] ${activeCat === c.id ? 'text-white/70' : 'text-[#6B7280]'}`}>(archived)</span>}
            </button>
            {isAdmin && (
              <div className={`flex items-center gap-0.5 ${activeCat === c.id ? 'opacity-90' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
                <button onClick={() => moveCat(idx, -1)} title="Move left" className="p-0.5 rounded hover:bg-black/10"><ChevronUp size={11} className="rotate-[-90deg]" /></button>
                <button onClick={() => moveCat(idx, 1)} title="Move right" className="p-0.5 rounded hover:bg-black/10"><ChevronDown size={11} className="rotate-[-90deg]" /></button>
                <button onClick={() => { setEditingCat(c); setCatName(c.name); setShowCatModal(true); }} title="Rename" className="p-0.5 rounded hover:bg-black/10"><Edit2 size={11} /></button>
                <button onClick={() => duplicateCat(c.id)} title="Duplicate" className="p-0.5 rounded hover:bg-black/10"><Copy size={11} /></button>
                {c.status === 'archived'
                  ? <button onClick={() => setCatStatus(c.id, 'active')} title="Restore" className="p-0.5 rounded hover:bg-black/10"><RotateCcw size={11} /></button>
                  : <button onClick={() => setCatStatus(c.id, 'archived')} title="Archive" className="p-0.5 rounded hover:bg-black/10"><Archive size={11} /></button>}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Templates of active category */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-[#6B7280]">{catTemplates.length} templates</p>
        {isAdmin && activeCat && (
          <button onClick={() => { setTmplName(''); setShowTmplModal(true); }} data-testid="add-template-btn"
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#3A8C8C] text-white text-xs font-medium hover:bg-[#2E7A7A] transition-colors">
            <Plus size={13} /> New Template
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {catTemplates.map(t => {
          const roomCount = (t.rooms || []).length;
          const itemCount = (t.rooms || []).reduce((s, r) => s + (r.items?.length || 0), 0);
          const archived = t.status === 'archived';
          return (
            <div key={t.id} className={`glass-card rounded-xl p-4 group ${archived ? 'opacity-60' : ''}`} data-testid={`template-card-${t.id}`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <h4 className="text-sm font-semibold text-[#2C2C2E]">{t.name}</h4>
                    {t.is_default && <Badge className="bg-[#3A8C8C]/10 text-[#3A8C8C] text-[9px] flex items-center gap-0.5"><Star size={9} /> Default</Badge>}
                    {archived && <Badge className="bg-[#E8E6E1] text-[#6B7280] text-[9px]">Archived</Badge>}
                  </div>
                  <p className="text-[11px] text-[#6B7280] mt-1">{roomCount} rooms · {itemCount} items</p>
                  {t.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {t.tags.map(tag => <span key={tag} className="text-[9px] px-1.5 py-0.5 rounded bg-[#F5F3EF] text-[#6B7280]">{tag}</span>)}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 mt-3 pt-3 border-t border-[#E8E6E1]">
                <button onClick={() => setEditing({ template: t, readOnly: !isAdmin })} data-testid={`template-edit-btn-${t.id}`}
                  className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#E8E6E1] text-[#2C2C2E] text-[11px] font-medium hover:bg-[#D4D0C8] transition-colors">
                  {isAdmin ? <><Edit2 size={11} /> Edit</> : <>View</>}
                </button>
                {isAdmin && !archived && (
                  <>
                    <button onClick={() => setDefault(t.id)} title="Set as default" className="p-1.5 rounded-lg hover:bg-[#3A8C8C]/10" data-testid={`template-default-btn-${t.id}`}><Star size={13} className={t.is_default ? 'text-[#3A8C8C] fill-[#3A8C8C]' : 'text-[#6B7280]'} /></button>
                    <button onClick={() => duplicateTemplate(t.id)} title="Duplicate" className="p-1.5 rounded-lg hover:bg-[#E8E6E1]"><Copy size={13} className="text-[#6B7280]" /></button>
                    <button onClick={() => archiveTemplate(t.id)} title="Archive" className="p-1.5 rounded-lg hover:bg-[#C4727F]/10"><Archive size={13} className="text-[#C4727F]" /></button>
                  </>
                )}
                {isAdmin && archived && (
                  <button onClick={() => restoreTemplate(t.id)} title="Restore" className="p-1.5 rounded-lg hover:bg-[#3A8C8C]/10"><RotateCcw size={13} className="text-[#3A8C8C]" /></button>
                )}
              </div>
            </div>
          );
        })}
        {catTemplates.length === 0 && (
          <div className="col-span-full text-center py-10 border border-dashed border-[#D4D0C8] rounded-xl">
            <Layers size={28} strokeWidth={1.5} className="text-[#D4D0C8] mx-auto mb-2" />
            <p className="text-sm text-[#6B7280]">No templates in this category</p>
          </div>
        )}
      </div>

      {/* Category modal */}
      <Dialog open={showCatModal} onOpenChange={setShowCatModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-['Manrope']">{editingCat ? 'Rename Category' : 'New Category'}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <Input value={catName} onChange={e => setCatName(e.target.value)} placeholder="Category name" autoFocus
              className="h-10 rounded-xl border-[#D4D0C8]" data-testid="category-name-input"
              onKeyDown={e => e.key === 'Enter' && saveCat()} />
            <button onClick={saveCat} className="w-full h-10 bg-[#3A8C8C] text-white rounded-full font-medium text-sm hover:bg-[#2E7A7A]" data-testid="save-category-btn">
              {editingCat ? 'Update' : 'Create'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* New template modal */}
      <Dialog open={showTmplModal} onOpenChange={setShowTmplModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-['Manrope']">New Template</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <Input value={tmplName} onChange={e => setTmplName(e.target.value)} placeholder="e.g. 2BHK Premium" autoFocus
              className="h-10 rounded-xl border-[#D4D0C8]" data-testid="template-name-input"
              onKeyDown={e => e.key === 'Enter' && createTemplate()} />
            <button onClick={createTemplate} className="w-full h-10 bg-[#3A8C8C] text-white rounded-full font-medium text-sm hover:bg-[#2E7A7A]" data-testid="create-template-btn">
              Create &amp; Edit
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Editor */}
      {editing && (
        <TemplateBOQEditor open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}
          template={editing.template} readOnly={editing.readOnly} onSaved={load} />
      )}

      {confirmDialog}
    </div>
  );
}
